import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { resolveRouteContext } from '../config/chatbotRouteMap';
import { getComponentContext } from '../config/chatbotContextMap';
import { useSelfHelpChat } from '../hooks/useSelfHelpChat';
import { useAuth } from './AuthContext';
import { apiFetch } from '../services/apiClient';

const ChatbotContext = createContext(null);

export function ChatbotProvider({ children }) {
  const { getToken } = useAuth();
  const location = useLocation();

  const [isOpen, setIsOpen] = useState(false);
  const [activeSession, setActiveSession] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [componentContext, setComponentContext] = useState(null);
  const [contextError, setContextError] = useState(null);

  // Track latest session ID via ref to avoid stale closures in sendMessage
  const sessionIdRef = useRef(null);
  useEffect(() => { sessionIdRef.current = activeSession?.id ?? null; }, [activeSession]);

  const {
    isStreaming,
    streamingText,
    streamingCitations,
    error: streamError,
    sendMessage: sendToBackend,
    cancelStream,
  } = useSelfHelpChat({ getToken });

  // Derive page context from route
  const pageContext = resolveRouteContext(location.pathname);

  // Build full context for API calls
  const buildContext = useCallback(() => {
    return {
      route: location.pathname,
      domain: pageContext.domain,
      epic: pageContext.epic,
      persona: pageContext.persona,
      ...(componentContext?.componentId ? { componentId: componentContext.componentId } : {}),
    };
  }, [location.pathname, pageContext, componentContext]);

  // Open chat drawer, optionally with component context.
  // aiContext can be:
  //   - a chatbotContextMap key (e.g. "staffing.coverage-status") → mapped to featureId + suggested Qs
  //   - a plain section title (e.g. "Budget Cards") → used as-is for KB search context
  const openChat = useCallback((aiContext) => {
    if (aiContext) {
      const mapped = getComponentContext(aiContext);
      if (mapped) {
        setComponentContext({ ...mapped, componentId: aiContext });
      } else {
        // Title-based context — no mapped suggestions, but pass the title for KB search
        setComponentContext({ componentId: aiContext, suggested: [`Tell me about ${aiContext}`] });
      }
    } else {
      setComponentContext(null);
    }
    setIsOpen(true);
  }, []);

  const closeChat = useCallback(() => {
    setIsOpen(false);
  }, []);

  // Start a new chat — clears local state only (session created lazily on first message)
  const startNewSession = useCallback(() => {
    setActiveSession({ id: null, title: 'New conversation', messages: [], context: buildContext() });
    setContextError(null);
  }, [buildContext]);

  // Create session on backend (called lazily from sendMessage)
  const ensureSession = useCallback(async () => {
    try {
      const response = await apiFetch('/api/chatbot/sessions', {
        method: 'POST',
        body: JSON.stringify({ context: buildContext() }),
      }, getToken);
      const sessionId = response.sessionId;
      setActiveSession((prev) => ({ ...prev, id: sessionId }));
      return sessionId;
    } catch (err) {
      console.error('Failed to create session:', err);
      return null;
    }
  }, [getToken, buildContext]);

  // Send a message
  const sendMessage = useCallback(async (text) => {
    setContextError(null);

    // Add user message to local state immediately (before any API calls)
    const userMsg = { role: 'user', content: text, timestamp: new Date().toISOString() };
    setActiveSession((prev) => ({
      ...prev,
      id: prev?.id ?? null,
      messages: [...(prev?.messages ?? []), userMsg],
    }));

    // Use ref to avoid stale closure on activeSession
    let sessionId = sessionIdRef.current;
    if (!sessionId) {
      sessionId = await ensureSession();
      if (!sessionId) {
        setContextError('Unable to connect to NexoNurse Assistant. Please check your connection and try again.');
        return;
      }
    }

    // Send to backend and get streamed response
    const result = await sendToBackend(sessionId, text, buildContext());

    if (result?.text) {
      const assistantMsg = {
        role: 'assistant',
        content: result.text,
        timestamp: new Date().toISOString(),
        messageId: result.messageId,
        citations: result.citations ?? [],
      };
      setActiveSession((prev) => ({
        ...prev,
        messages: [...(prev?.messages ?? []), assistantMsg],
      }));
    }
  }, [ensureSession, sendToBackend, buildContext]);

  // Load existing session
  const loadSession = useCallback(async (sessionId) => {
    try {
      const session = await apiFetch(`/api/chatbot/sessions/${sessionId}`, {}, getToken);
      setActiveSession({
        id: session.sessionId,
        title: session.title,
        messages: session.messages ?? [],
        context: session.context,
      });
    } catch (err) {
      console.error('Failed to load session:', err);
    }
  }, [getToken]);

  // Delete session
  const deleteSession = useCallback(async (sessionId) => {
    try {
      await apiFetch(`/api/chatbot/sessions/${sessionId}`, { method: 'DELETE' }, getToken);
      setSessions((prev) => prev.filter((s) => s.sessionId !== sessionId));
      if (activeSession?.id === sessionId) {
        setActiveSession(null);
      }
    } catch (err) {
      console.error('Failed to delete session:', err);
    }
  }, [getToken, activeSession]);

  // Listen for SectionHeading AI icon clicks (custom DOM event)
  useEffect(() => {
    const handler = (e) => openChat(e.detail);
    window.addEventListener('nexonurse:open-chatbot', handler);
    return () => window.removeEventListener('nexonurse:open-chatbot', handler);
  }, [openChat]);

  // Fetch sessions list when drawer opens
  useEffect(() => {
    if (!isOpen) return;
    (async () => {
      try {
        const data = await apiFetch('/api/chatbot/sessions', {}, getToken);
        setSessions(data.sessions ?? []);
      } catch {
        // Silently fail — sessions list is non-critical
      }
    })();
  }, [isOpen, getToken]);

  // Submit feedback
  const submitFeedback = useCallback(async (messageId, rating, comment) => {
    try {
      await apiFetch('/api/chatbot/feedback', {
        method: 'POST',
        body: JSON.stringify({
          messageId,
          sessionId: activeSession?.id,
          rating,
          comment,
        }),
      }, getToken);
    } catch (err) {
      console.error('Failed to submit feedback:', err);
    }
  }, [getToken, activeSession]);

  const value = {
    isOpen,
    activeSession,
    sessions,
    isStreaming,
    streamingText,
    streamingCitations,
    streamError: streamError || contextError,
    pageContext,
    componentContext,
    openChat,
    closeChat,
    sendMessage,
    cancelStream,
    startNewSession,
    loadSession,
    deleteSession,
    submitFeedback,
  };

  return <ChatbotContext.Provider value={value}>{children}</ChatbotContext.Provider>;
}

export function useChatbot() {
  const ctx = useContext(ChatbotContext);
  if (!ctx) throw new Error('useChatbot must be used within ChatbotProvider');
  return ctx;
}
